import { useEffect, useRef } from 'react';
import { NetworkStatus } from '@apollo/client';
import { useCheckoutDialogContext } from '../../guest/components/CheckoutDialog';
import { useGetItemCheckoutMechanismsQuery, ShippingAddressInput } from '@graphql/generated';

const JOY_ADDRESS: ShippingAddressInput = {
  name: 'Joy HQ',
  address1: '939 Harrison St',
  city: 'San Francisco',
  state: 'CA',
  postalCode: '94103',
  country: 'United States',
  validated: true,
  countryCode: 'USA'
};

type CheckoutMechanismsProps = Readonly<{
  productId: Readonly<string>;
}>;

export const isDropshippable = (registryItem: Maybe<{ productData: Maybe<{ checkoutMechanisms: Maybe<Array<{ type: string }>> }> }>) => {
  return !!registryItem?.productData?.checkoutMechanisms?.some(mechanism => mechanism?.type === 'joyWeb');
};

// CHECKOUT MECHANISMS FLOW
// -- First step --
// Checkout mechanisms query (called automatically)
// Variables: product id
// Result: checkoutMechanisms.type === 'joyWeb' ? isDropshipable = true && isOutOfStock = false. No more steps are needed

// -- Second step --
// Check shipping address from registry state
// Result: couple has address ? isDropshipable = false && isOutOfStock = true. No more steps are needed

// -- Third step --
// Refetch query (called through useEffect)
// Variables: product id and a hardcoded address (Joys' address)
// Result: checkoutMechanisms.type !== 'joyWeb' ? isDropshipable = false && isOutOfStock = checkoutMechanisms is empty []. No more steps are needed

// -- Fourth step --
// Refetch query (called through checkoutMechanismsWithAltAddress)
// Variables: product id and a shipping address entered by the guest
// Result: isDropshipable = checkoutMechanisms.type === 'joyWeb'

export const useCheckoutMechanisms = ({ productId }: CheckoutMechanismsProps) => {
  const isQueryWithJoyAddressAvailable = useRef(true);

  const { registryState } = useCheckoutDialogContext();
  const shippingAddress = registryState?.shippingAddress;

  const { data, loading, refetch, networkStatus } = useGetItemCheckoutMechanismsQuery({
    batchMode: 'fast',
    notifyOnNetworkStatusChange: true,
    variables: { id: productId }
  });
  const isDropshipable = isDropshippable(data?.registryItem);
  const isOutOfStock = data?.registryItem?.productData?.checkoutMechanisms?.length === 0;

  const checkoutMechanismsWithAltAddress = async (altAddress: ShippingAddressInput): Promise<boolean> => {
    const queryResult = await refetch({ id: productId, shippingAddress: altAddress });
    return !!queryResult?.data?.registryItem?.productData?.checkoutMechanisms.some(mechanism => mechanism.type === 'joyWeb');
  };

  const hideValueProps = isDropshipable && (data?.registryItem?.productData.checkoutMechanisms.find(mechanism => mechanism.type === 'joyWeb')?.hideValueProps ?? false);

  useEffect(() => {
    if (!!data && !isDropshipable && !shippingAddress && isQueryWithJoyAddressAvailable.current) {
      isQueryWithJoyAddressAvailable.current = false;
      refetch({ id: productId, shippingAddress: JOY_ADDRESS });
    }
  }, [data, isDropshipable, isQueryWithJoyAddressAvailable, productId, refetch, shippingAddress]);

  useEffect(() => {
    if (isQueryWithJoyAddressAvailable.current === false) {
      isQueryWithJoyAddressAvailable.current = true;
    }
  }, [productId]);

  return {
    isLoading: loading || networkStatus === NetworkStatus.fetchMore,
    isDropshipable,
    isOutOfStock,
    hideValueProps,
    checkoutMechanismsWithAltAddress
  };
};
